import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, Loader2, Phone } from "lucide-react";
import axios from "axios";
import { useUser } from "@/contexts/UserContext";
import { startGoogleLogin } from "@/lib/googleAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setUserData, isLoggedIn } = useUser();
  const [mode, setMode] = useState(() => {
    // /signup or /register → default to register mode; /login → login
    const p = (location?.pathname || "").toLowerCase();
    return (p.includes("signup") || p.includes("register")) ? "register" : "login";
  }); // login, register, forgot
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Get redirect URL from query params or state
  const redirectTo = searchParams.get('redirect') || location.state?.from || "/dashboard";
  
  // If already logged in, redirect
  useEffect(() => {
    if (isLoggedIn) {
      navigate(redirectTo);
    }
  }, [isLoggedIn, navigate, redirectTo]);
  
  // Form data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    rememberMe: false
  });

  const handleChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const [showGoogleConfirm, setShowGoogleConfirm] = useState(false);

  const handleGoogleLogin = () => {
    // Custom Markeemix Google OAuth (no third-party broker)
    startGoogleLogin();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please fill all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login/email`, {
        email: formData.email,
        password: formData.password,
        remember_me: formData.rememberMe
      }, { withCredentials: true });

      if (response.data.success) {
        // Check if 2FA OTP verification is required
        if (response.data.requires_otp) {
          toast.info("OTP sent to your email");
          navigate("/verify-otp", { 
            state: { 
              email: response.data.email,
              otpHint: response.data.otp_hint // For testing only
            } 
          });
        } else {
          // Set user data in context immediately
          setUserData(response.data.user, response.data.session_token);
          toast.success("Login successful!");
          
          // Check if user came from trial flow and doesn't have trial yet
          const startTrial = searchParams.get('trial') === 'true';
          if (startTrial && response.data.user?.plan === 'free' && !response.data.user?.trial_used) {
            try {
              const trialResponse = await axios.post(`${API_URL}/api/auth/start-trial`, {}, { withCredentials: true });
              if (trialResponse.data.success) {
                toast.success("7-day free trial activated! Enjoy Pro features!");
                setUserData({ ...response.data.user, plan: "pro", trial_active: true });
              }
            } catch (trialError) {
              console.error("Trial activation failed:", trialError);
            }
          }
          
          // If no explicit redirect URL was given, ask the backend where
          // this user should land based on their primary studio role.
          let dest = redirectTo;
          if (!searchParams.get("redirect")) {
            try {
              const r = await axios.get(`${API_URL}/api/studio/crm/role-home`, { withCredentials: true });
              if (r.data?.home) dest = r.data.home;
            } catch {/* fall back to default */}
          }
          navigate(dest);
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Login failed";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill all fields");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null  // Optional phone for WhatsApp
      }, { withCredentials: true });

      if (response.data.success) {
        // Set user data in context immediately
        setUserData(response.data.user, response.data.session_token);
        toast.success("Account created successfully!");
        
        // Check if user came from trial flow
        const startTrial = searchParams.get('trial') === 'true';
        if (startTrial) {
          try {
            const trialResponse = await axios.post(`${API_URL}/api/auth/start-trial`, {}, { withCredentials: true });
            if (trialResponse.data.success) {
              toast.success("7-day free trial activated! Enjoy Pro features!");
              // Update user data with trial status
              setUserData({ ...response.data.user, plan: "pro", trial_active: true });
            }
          } catch (trialError) {
            console.error("Trial activation failed:", trialError);
            // Don't block registration if trial fails
          }
        }
        
        navigate(redirectTo);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email: formData.email
      });

      if (response.data.success) {
        toast.success("Password reset link sent to your email!");
        setMode("login");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#FAF5FF] flex items-center justify-center p-4" data-testid="auth-page">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-violet-500/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md p-8 relative z-10 shadow-2xl bg-white border border-slate-200 text-slate-900">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="absolute top-4 left-4 text-[#9aa4b6] hover:text-[#e9ecf3] flex items-center gap-1 text-sm"
          data-testid="auth-back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-[#a78bfa] to-[#ec7c5e] bg-clip-text text-transparent">
            MarkeeMix
          </h1>
          <p className="text-[#9aa4b6] text-sm mt-1">
            {mode === "login" && "Welcome back!"}
            {mode === "register" && "Create your account"}
            {mode === "forgot" && "Reset your password"}
          </p>
        </div>

        {/* Login Form */}
        {mode === "login" && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                name="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                className="pl-10"
                data-testid="login-email-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                className="pl-10 pr-10"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="login-password-toggle"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <div className="flex justify-between items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  data-testid="login-remember-checkbox"
                />
                <span className="text-sm text-gray-600">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => setMode("forgot")}
                className="text-sm text-violet-600 hover:underline"
                data-testid="login-forgot-button"
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
              data-testid="login-google-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-gray-500 mt-6">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => setMode("register")}
                className="text-violet-600 font-medium hover:underline"
                data-testid="login-switch-register"
              >
                Sign up
              </button>
            </p>
          </form>
        )}

        {/* Register Form */}
        {mode === "register" && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                name="name"
                placeholder="Full name"
                value={formData.name}
                onChange={handleChange}
                className="pl-10"
                data-testid="register-name-input"
              />
            </div>

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                name="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                className="pl-10"
                data-testid="register-email-input"
              />
            </div>

            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="tel"
                name="phone"
                placeholder="WhatsApp number (optional, with country code)"
                value={formData.phone || ""}
                onChange={handleChange}
                className="pl-10"
                data-testid="register-phone-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password (min 6 characters)"
                value={formData.password}
                onChange={handleChange}
                className="pl-10 pr-10"
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="register-password-toggle"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type={showPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="pl-10"
                data-testid="register-confirm-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Account"}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
              data-testid="register-google-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-gray-500 mt-6">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => setMode("login")}
                className="text-violet-600 font-medium hover:underline"
                data-testid="register-switch-login"
              >
                Sign in
              </button>
            </p>
          </form>
        )}

        {/* Forgot Password Form */}
        {mode === "forgot" && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <p className="text-gray-600 text-sm mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                name="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                className="pl-10"
                data-testid="forgot-email-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              disabled={loading}
              data-testid="forgot-submit-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}
            </Button>

            <p className="text-center text-sm text-gray-500 mt-6">
              Remember your password?{" "}
              <button
                type="button"
                onClick={() => setMode("login")}
                className="text-violet-600 font-medium hover:underline"
                data-testid="forgot-switch-login"
              >
                Sign in
              </button>
            </p>
          </form>
        )}
      </Card>
    </div>
  );
}
